import React from 'react';
import { THEME } from '../../utils/theme';
import BarChart from './BarChart';
import RingStat from './RingStat';

const StatsDashboard = ({ stats, onClose }) => {
  if (!stats) return null;

  const formatHours = (mins) => Math.round(mins / 60).toLocaleString();

  // Use dynamic user_stats from API when available
  const userStatsEntries = Object.entries(stats.user_stats || {});

  // Build status data for each user dynamically
  const userStatusData = userStatsEntries.map(([username, userData]) => ({
    username,
    displayName: userData.display_name,
    color: userData.color || THEME.success,
    icon: userData.icon,
    playtime: userData.playtime || 0,
    statusData: [
      { label: 'Played', value: userData.played || 0, color: THEME.success },
      { label: 'WTP', value: userData.wtp || 0, color: THEME.warning },
      { label: 'WTF', value: userData.wtf || 0, color: THEME.pink },
      { label: 'Never', value: userData.never || 0, color: THEME.danger },
      { label: 'New', value: userData.unplayed || 0, color: THEME.textDim },
    ]
  }));

  const maxStatusValue = Math.max(...userStatusData.flatMap(u => u.statusData.map(d => d.value)));

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(5,5,10,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(20px) saturate(180%)', WebkitBackdropFilter: 'blur(20px) saturate(180%)' }} onClick={onClose}>
      <div style={{ background: `linear-gradient(145deg, ${THEME.bgElevated}, ${THEME.bgCard})`, borderRadius: '20px', padding: '28px', width: '95%', maxWidth: '900px', maxHeight: '90vh', overflow: 'auto', border: `1px solid ${THEME.border}`, boxShadow: `0 25px 80px ${THEME.shadowHeavy}`, animation: 'scaleIn 0.2s ease' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ margin: 0, color: THEME.text, fontSize: '24px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span>📊</span> Stats Dashboard
          </h2>
          <button onClick={onClose} style={{ background: THEME.bgHover, border: 'none', color: THEME.textMuted, width: '36px', height: '36px', borderRadius: '10px', fontSize: '20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>&times;</button>
        </div>

        {/* Top-level summary */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '16px', marginBottom: '28px' }}>
          {[
            { label: 'Total Games', value: stats.total_games, color: THEME.text },
            { label: 'Online MP', value: stats.online_mp_games || 0, color: THEME.accent },
            { label: 'Rated Games', value: stats.rated_games, color: THEME.success },
            { label: 'Total Sessions', value: stats.total_sessions, color: THEME.info },
          ].map((item, i) => (
            <div key={i} style={{ background: THEME.bg, borderRadius: '12px', padding: '18px', textAlign: 'center', border: `1px solid ${THEME.border}` }}>
              <div style={{ fontSize: '28px', fontWeight: '700', color: item.color, marginBottom: '4px' }}>{item.value}</div>
              <div style={{ fontSize: '11px', color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{item.label}</div>
            </div>
          ))}
        </div>

        {/* Playtime comparison - dynamic for all users */}
        <div style={{ background: THEME.bg, borderRadius: '14px', padding: '20px', marginBottom: '24px', border: `1px solid ${THEME.border}` }}>
          <h3 style={{ margin: '0 0 16px', color: THEME.text, fontSize: '15px', fontWeight: '600' }}>Playtime by User</h3>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '40px', flexWrap: 'wrap' }}>
            {userStatusData.map(userData => (
              <div key={userData.username} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: '700', color: userData.color }}>{formatHours(userData.playtime)}h</div>
                <div style={{ fontSize: '11px', color: THEME.textMuted }}>{userData.icon || ''} {userData.displayName}</div>
              </div>
            ))}
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: THEME.text }}>{formatHours(stats.combined_playtime || 0)}h</div>
              <div style={{ fontSize: '11px', color: THEME.textMuted }}>Combined</div>
            </div>
          </div>
        </div>

        {/* Status breakdown by user - dynamic */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '20px', marginBottom: '24px' }}>
          {userStatusData.map(userData => (
            <div key={userData.username} style={{ background: THEME.bg, borderRadius: '14px', padding: '20px', border: `1px solid ${userData.color}40` }}>
              <h3 style={{ margin: '0 0 16px', color: userData.color, fontSize: '15px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: userData.color }} /> {userData.icon || ''} {userData.displayName} Status
              </h3>
              <BarChart data={userData.statusData} maxValue={maxStatusValue} />
            </div>
          ))}
        </div>

        {/* Matching games section */}
        <div style={{ background: THEME.bg, borderRadius: '14px', padding: '20px', marginBottom: '24px', border: `1px solid ${THEME.border}` }}>
          <h3 style={{ margin: '0 0 16px', color: THEME.text, fontSize: '15px', fontWeight: '600' }}>Games Ready to Play Together</h3>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '24px', flexWrap: 'wrap' }}>
            <RingStat value={stats.both_wtp || 0} max={stats.total_games} color={THEME.warning} label="Both WTP" />
            <RingStat value={stats.both_played || 0} max={stats.total_games} color={THEME.success} label="Both Played" />
            <RingStat value={stats.rated_games || 0} max={stats.total_games} color={THEME.accent} label="Rated" />
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '32px', marginTop: '20px', paddingTop: '16px', borderTop: `1px solid ${THEME.border}` }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '20px', fontWeight: '700', color: THEME.warning }}>{stats.both_wtp || 0}</div>
              <div style={{ fontSize: '11px', color: THEME.textMuted }}>Both Want to Play</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '20px', fontWeight: '700', color: THEME.success }}>{stats.both_played || 0}</div>
              <div style={{ fontSize: '11px', color: THEME.textMuted }}>Both Played</div>
            </div>
          </div>
        </div>

        {/* Ratings comparison - Dynamic based on users */}
        <div style={{ background: THEME.bg, borderRadius: '14px', padding: '20px', border: `1px solid ${THEME.border}` }}>
          <h3 style={{ margin: '0 0 16px', color: THEME.text, fontSize: '15px', fontWeight: '600' }}>Average Ratings</h3>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '40px', flexWrap: 'wrap' }}>
            {userStatsEntries.map(([username, userData], idx) => (
              <React.Fragment key={username}>
                {idx > 0 && <div style={{ width: '1px', background: THEME.border }} />}
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '36px', fontWeight: '700', color: userData.color || THEME.success }}>{userData.avg_rating || '—'}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}>
                    <span style={{ color: '#fbbf24', fontSize: '16px' }}>★</span>
                    <span style={{ fontSize: '12px', color: THEME.textMuted }}>{userData.icon || ''} {userData.display_name} Avg</span>
                  </div>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>

        {stats.last_sync && (
          <div style={{ marginTop: '20px', textAlign: 'center', color: THEME.textDim, fontSize: '12px' }}>
            Last synced: {new Date(stats.last_sync).toLocaleString()}
          </div>
        )}
      </div>
    </div>
  );
};

export default StatsDashboard;
